import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiEyeLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminCategory } from '@/hooks/admin/useAdmin';
import { useCategories, useDeleteCategory } from '@/hooks/admin/useAdminCategories';
import CategoryModal from '@/modals/admin/CategoryModal';

const CategoriesAdmin: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<AdminCategory | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');

    const { data: categories = [], isLoading, error } = useCategories();
    const deleteCategory = useDeleteCategory();

    const filteredCategories = categories.filter(category => {
        const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            category.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterActive === 'all' ||
                            (filterActive === 'active' && category.is_active) ||
                            (filterActive === 'inactive' && !category.is_active);
        return matchesSearch && matchesFilter;
    });

    const handleCreateCategory = () => {
        setSelectedCategory(undefined);
        setModalOpen(true);
    };

    const handleEditCategory = (category: AdminCategory) => {
        setSelectedCategory(category);
        setModalOpen(true);
    };

    const handleDeleteCategory = async (category: AdminCategory) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar la categoría "${category.name}"?`)) {
            try {
                await deleteCategory.mutateAsync(category.id);
            } catch (error) {
                console.error('Error deleting category:', error);
                alert('Error al eliminar la categoría');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedCategory(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando categorías...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar las categorías</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Categorías</h1>
                        <p className="admin-page-subtitle">
                            Administra las categorías de productos de tu tienda
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateCategory}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nueva Categoría
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar categorías..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todas</option>
                        <option value="active">Activas</option>
                        <option value="inactive">Inactivas</option>
                    </select>
                </div>
            </div>

            {/* Categories Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Imagen</th>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th>Orden</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCategories.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all'
                                        ? 'No se encontraron categorías con los filtros aplicados'
                                        : 'No hay categorías registradas'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredCategories.map(category => (
                                <tr key={category.id}>
                                    <td className="admin-table-image">
                                        {category.image_url ? (
                                            <img
                                                src={category.image_url}
                                                alt={category.name}
                                                className="admin-category-image"
                                            />
                                        ) : (
                                            <div className="admin-no-image">
                                                <RiEyeLine />
                                            </div>
                                        )}
                                    </td>
                                    <td className="admin-table-name">
                                        {category.name}
                                    </td>
                                    <td className="admin-table-description">
                                        {category.description ? (
                                            <span title={category.description}>
                                                {category.description.length > 50
                                                    ? `${category.description.substring(0, 50)}...`
                                                    : category.description
                                                }
                                            </span>
                                        ) : (
                                            <span className="admin-no-description">Sin descripción</span>
                                        )}
                                    </td>
                                    <td className="admin-table-order">
                                        {category.sort_order}
                                    </td>
                                    <td>
                                        <span className={`admin-status ${category.is_active ? 'active' : 'inactive'}`}>
                                            {category.is_active ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditCategory(category)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteCategory(category)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteCategory.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination would go here if needed */}

            {/* Category Modal */}
            <CategoryModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                category={selectedCategory}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default CategoriesAdmin;
